/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { Vector3, Quaternion, Vector2, PerspectiveCamera, Mesh } from 'three';
import { AffineTransform } from './AffineTransform';
import { Pointer } from '../../Pointer';
import { UndoStack } from './UndoStack';
import { Events } from '../../Events';
import { GizmoEventType } from './GizmoEventType';
import { vectorAbs } from './GizmoKitUtils';
/**
 * the gizmo state
 */
export var GizmoState;
(function (GizmoState) {
    GizmoState[GizmoState["NeedsObject"] = 0] = "NeedsObject";
    GizmoState[GizmoState["Idle"] = 1] = "Idle";
    GizmoState[GizmoState["Rotating"] = 2] = "Rotating";
    GizmoState[GizmoState["Translating"] = 3] = "Translating";
    GizmoState[GizmoState["Scaling"] = 4] = "Scaling";
})(GizmoState || (GizmoState = {}));
/**
 * Gizmo orientation mode
 */
export var GizmoMode;
(function (GizmoMode) {
    GizmoMode[GizmoMode["Local"] = 0] = "Local";
    GizmoMode[GizmoMode["Global"] = 1] = "Global";
    GizmoMode[GizmoMode["View"] = 2] = "View";
})(GizmoMode || (GizmoMode = {}));
/**
 * the operation
 */
export var Operation;
(function (Operation) {
    Operation[Operation["Rotate"] = 0] = "Rotate";
    Operation[Operation["Translate"] = 1] = "Translate";
    Operation[Operation["Scale"] = 2] = "Scale";
})(Operation || (Operation = {}));
/**
 * Degree of Freedom for the Operation
 */
export var OperationDOF;
(function (OperationDOF) {
    OperationDOF[OperationDOF["NON"] = 0] = "NON";
    OperationDOF[OperationDOF["X"] = 1] = "X";
    OperationDOF[OperationDOF["Y"] = 2] = "Y";
    OperationDOF[OperationDOF["XY"] = 3] = "XY";
    OperationDOF[OperationDOF["Z"] = 4] = "Z";
    OperationDOF[OperationDOF["ZX"] = 5] = "ZX";
    OperationDOF[OperationDOF["YZ"] = 6] = "YZ";
    OperationDOF[OperationDOF["XYZ"] = 7] = "XYZ";
    OperationDOF[OperationDOF["ALL"] = 8] = "ALL";
})(OperationDOF || (OperationDOF = {}));
/**
 * Base functionality for editors (that handles the object as a whole )
 */
export class GizmoKit {
    /**
     * constructor
     * @param camera camera of the scene that shows the editor (gizmo)
     * @param gizmo the gizmo itself (the thing that can handle transformation)
     * @param object an initial object that should be manipulated (can also be setted later)
     */
    constructor(camera, gizmo, object) {
        /**
         * returns the dot products (angles) between the gizmo axes and the camera world direction
         * @returns Vector3
         */
        this.checkGizmoAxes = (() => {
            const _dir = new Vector3();
            const _rot = new Quaternion();
            return () => {
                if (this._camera instanceof PerspectiveCamera) {
                    _dir.copy(this._gizmo.position).sub(this._camera.position).normalize();
                }
                else {
                    this._camera.getWorldDirection(_dir);
                }
                this._gizmo.getWorldQuaternion(_rot);
                _rot.inverse();
                _dir.applyQuaternion(_rot);
                return _dir;
            };
        })();
        this._gizmoState = GizmoState.NeedsObject;
        this._mode = GizmoMode.Local;
        this._undoStack = new UndoStack();
        /**
         * translates a vector from global to local space
         * @param vector
         * @param global
         * @param local
         */
        this._globalToLocalVector = (vector, global, local) => {
            vector.applyQuaternion(global.clone().inverse());
            if (local) {
                vector.applyQuaternion(local);
            }
            return vector;
        };
        /**
         * return the current relevant gizmo axis
         * @param ctx
         */
        this._getActiveGizmoAxis = (ctx) => {
            return this._getBestAxisFromDOF(ctx.dof).applyQuaternion(ctx.gizmoStart.rotation);
        };
        /**
         * if there is one axis in DOF this axis as global vector will be returned
         * if there are 2 axes in DOF the third (other) will be returned (normal)
         * else the x axis will be returned
         * @param dof // degree of freedom
         */
        this._getBestAxisFromDOF = (dof) => {
            switch (dof) {
                case OperationDOF.Y:
                case OperationDOF.ZX:
                    return new Vector3(0, 1, 0);
                case OperationDOF.Z:
                case OperationDOF.XY:
                    return new Vector3(0, 0, 1);
                case OperationDOF.X:
                case OperationDOF.YZ:
                default:
                    return new Vector3(1, 0, 0);
            }
        };
        this._camera = camera;
        this._gizmo = gizmo;
        this.setObject(object);
    }
    /**
     * sets the mode (orientation of the gizmo)
     * @param mode local (operations are done in objects local space), global (operations are done in global space) or view (orthogonal to camera)
     */
    setMode(mode) {
        this._mode = mode;
        this._followObject();
    }
    /**
     * returns the mode
     */
    getMode() {
        return this._mode;
    }
    /**
     * sets the object3d that should be manipulated
     * @param object
     */
    setObject(object) {
        const last = this._setObjectRaw(object);
        if (last !== object) { // only if not same object
            this._dispatchTransformEvent((object == null) ? GizmoEventType.DESELECTED : GizmoEventType.SELECTED, undefined, undefined, last);
            this._pushUndo();
        }
    }
    /**
     * sets the camera
     * @param camera
     */
    setCamera(camera) {
        this._camera = camera;
    }
    /**
     * sets general locks (locks for all objects)
     * @param lock
     */
    setGeneralLock(lock) {
        this._generalLock = lock;
    }
    /**
     * return the general locks
     */
    getGeneralLock() {
        return this._generalLock;
    }
    /**
     * returns the object specific locks
     */
    getObjectLock() {
        return (this._object) ? this._object.userData.gizmoLock : undefined;
    }
    /**
     * returns all locks for a special operation, for a special mode
     * @param op the operation
     * @param mode the mode
     */
    getLocksFor(op, mode) {
        const gLocks = this.extractLock(this.getGeneralLock(), op, mode);
        const oLocks = this.extractLock(this.getObjectLock(), op, mode);
        const locks = new Set(gLocks);
        oLocks.forEach((e) => locks.add(e));
        return locks;
    }
    /**
     * extract all possible degree of freedom for a special operation and mode
     * @param lock the lock to extract from
     * @param op the operation
     * @param mode the mode
     */
    extractLock(lock, op, mode) {
        let mOut;
        let dofsOut;
        if (lock) {
            switch (mode) {
                case GizmoMode.Global:
                    mOut = lock.global;
                    break;
                case GizmoMode.Local:
                    mOut = lock.local;
                    break;
                case GizmoMode.View:
                    mOut = lock.local;
                    break;
                default:
            }
            if (mOut) {
                switch (op) {
                    case Operation.Translate:
                        dofsOut = mOut.translate;
                        break;
                    case Operation.Scale:
                        dofsOut = mOut.scale;
                        break;
                    case Operation.Rotate:
                        dofsOut = mOut.rotate;
                        break;
                    default:
                }
            }
        }
        return dofsOut ? dofsOut : [];
    }
    /**
     * checks if a special operation (with special dof and mode) is allowed
     * @param op the operation
     * @param dof degree of freedom
     * @param mode the mode, if undefined its the currend mode of the gizmoKit
     */
    lockedFor(op, dof, mode) {
        const m = mode || this._mode;
        const locks = this.getLocksFor(op, m);
        return locks.has(OperationDOF.ALL) || locks.has(dof);
    }
    /**
     * initializes the transformation
     * this function will change the state of the context!
     * @param ctx the Context (you have to init this context before!)
     * @param mousePos the mouse position
     */
    activate(ctx, mousePos) {
        if (this._gizmoState === GizmoState.NeedsObject) {
            // tslint:disable-next-line:no-console
            console.error('GizmoKit needs an object');
            return ctx;
        }
        ctx.locked = this.lockedFor(ctx.operation, ctx.dof);
        ctx.gizmoWorldStart.fromMatrix(this._gizmo.matrixWorld);
        ctx.gizmoStart.copyFromObject3D(this._gizmo);
        ctx.objectWorldStart.fromMatrix(this._object.matrixWorld);
        ctx.objectStart.copyFromObject3D(this._object);
        if (!ctx.surfaceCenter) {
            ctx.surfaceCenter = ctx.gizmoWorldStart.position;
        }
        if (!ctx.pointer) {
            const axis = this._getActiveGizmoAxis(ctx);
            ctx.pointer = this._getPointer(ctx.dof, ctx.surfaceCenter, axis, ctx.operation === Operation.Rotate);
        }
        ctx.surfaceMouseStart.copy(ctx.pointer.to3DSpace(mousePos.mousex, mousePos.mousey));
        if (ctx.operation === Operation.Scale && this._object instanceof Mesh) {
            const mesh = this._object;
            mesh.geometry.computeBoundingBox();
            mesh.geometry.boundingBox.getSize(ctx.referenceSize);
            ctx.referenceSize.multiply(ctx.objectStart.scale);
        }
        this._dispatchTransformEvent(GizmoEventType.ACTIVATED, ctx, undefined);
        return ctx;
    }
    /**
     * deactivates the current transformation
     */
    deactivate() {
        this._gizmoState = GizmoState.Idle;
        this._dispatchTransformEvent(GizmoEventType.DEACTIVATED, undefined, undefined);
        this._pushUndo();
    }
    /**
     * undo one step (transformation) back
     */
    undo() {
        const data = this._undoStack.undo();
        data.transform.copyToObject3D(data.object);
        this._setObjectRaw(data.object);
        this._gizmo.updateForObject(data.object);
    }
    /**
     * redo one step (transformation) fore [if you called undo before of cause]
     */
    redo() {
        const data = this._undoStack.redo();
        data.transform.copyToObject3D(data.object);
        this._setObjectRaw(data.object);
        this._gizmo.updateForObject(data.object);
    }
    /**
     * Check if undo is possible
     */
    canUndo() {
        return this._undoStack.canUndo();
    }
    /**
     * Check if redo is possible
     */
    canRedo() {
        return this._undoStack.canRedo();
    }
    /**
     * translates the object
     * @param ctx
     * @param mouse
     */
    translate(ctx, mouse) {
        const m = ctx.pointer.to3DSpace(mouse.mousex, mouse.mousey);
        m.sub(ctx.surfaceMouseStart).add(ctx.objectWorldStart.position);
        return this.setAbsolutePosition(ctx, m);
    }
    /**
     * translates the object
     * @param ctx
     * @param pos in world coordinates
     */
    setAbsolutePosition(ctx, pos) {
        const pnew = pos.clone();
        pnew.sub(ctx.objectWorldStart.position);
        return this.setRelativePosition(ctx, pnew);
    }
    /**
     * translates the object
     * @param ctx
     * @param offset (newWorldPosition = oldWorldPosition + offset)
     */
    setRelativePosition(ctx, offset) {
        const noffset = this._globalToLocalVector(offset, ctx.objectWorldStart.rotation, ctx.objectStart.rotation);
        let type = GizmoEventType.REJECTED;
        if (!ctx.locked) {
            this._object.position.copy(noffset.add(ctx.objectStart.position));
            this._followObject();
            type = GizmoEventType.TRANSFORMED;
        }
        const res = { angle: 0, offset: noffset.toArray(), scale: [] };
        this._dispatchTransformEvent(type, ctx, res);
        return res;
    }
    /**
     * rotates the object
     * @param ctx
     * @param mouse
     */
    rotate(ctx, mouse) {
        if (ctx.locked || ctx.dof !== OperationDOF.X && ctx.dof !== OperationDOF.Y && ctx.dof !== OperationDOF.Z) {
            // tslint:disable-next-line:no-console
            console.warn('The rotation can\'t be handled with the given degree of freedom: ' + ctx.dof.toString);
            return { angle: 0, offset: [], scale: [] };
        }
        const m = ctx.pointer.to3DSpace(mouse.mousex, mouse.mousey).sub(ctx.gizmoWorldStart.position);
        const local = m.applyQuaternion(ctx.gizmoWorldStart.rotation.clone().inverse());
        const components = this._extractComponents(local, this._complement(ctx.dof));
        const angle = new Vector2().fromArray(components).angle();
        return this.setRelativeAngle(ctx, angle);
    }
    /**
     * rotates the object
     * @param ctx
     * @param angle
     */
    setRelativeAngle(ctx, angle) {
        const laxis = this._getBestAxisFromDOF(ctx.dof);
        this._globalToLocalVector(laxis, ctx.objectWorldStart.rotation, ctx.gizmoWorldStart.rotation);
        const qRot = new Quaternion().setFromAxisAngle(laxis, angle);
        let type = GizmoEventType.REJECTED;
        if (!ctx.locked) {
            this._object.quaternion.copy(ctx.objectStart.rotation.clone().multiply(qRot));
            this._followObject();
            type = GizmoEventType.TRANSFORMED;
        }
        const res = { angle, offset: [], scale: [] };
        this._dispatchTransformEvent(type, ctx, res);
        return res;
    }
    /**
     * scales the object
     * @param ctx
     * @param mouse
     */
    scale(ctx, mouse, scaleConstraint) {
        const dof = (this._mode !== GizmoMode.Local) ? OperationDOF.XYZ : ctx.dof;
        const offset = ctx.pointer.to3DSpace(mouse.mousex, mouse.mousey).sub(ctx.surfaceMouseStart);
        const noffset = this._globalToLocalVector(offset, ctx.objectWorldStart.rotation);
        const change = this._getScaleVector(dof, scaleConstraint ? 1 : 2, 0).multiply(noffset);
        if (scaleConstraint) {
            this.setRelativePosition(ctx, change.clone().applyQuaternion(ctx.objectWorldStart.rotation).multiplyScalar(0.5));
            change.multiply(scaleConstraint);
        }
        const newSize = vectorAbs(ctx.referenceSize.clone().add(change));
        const newScale = newSize.divide(ctx.referenceSize);
        return this.setRelativScale(ctx, newScale);
    }
    /**
     * scales the object
     * @param ctx
     * @param relScale
     */
    setRelativScale(ctx, relScale) {
        let type = GizmoEventType.REJECTED;
        if (!ctx.locked) {
            this._object.scale.copy(ctx.objectStart.scale);
            this._object.scale.multiply(relScale);
            this._followObject();
            type = GizmoEventType.TRANSFORMED;
        }
        const res = { angle: 0, offset: [], scale: relScale.toArray() };
        this._dispatchTransformEvent(type, ctx, res);
        return res;
    }
    _dispatchTransformEvent(eventType, transformContext, changes, lastObject) {
        this._gizmo.dispatchEvent(new CustomEvent(Events.GIZMOTRANSFORM, { detail: {
                eventType,
                transformContext,
                editor: this._gizmo,
                object: this._object,
                change: changes,
                lastObject,
            } }));
    }
    _getScaleVector(dof, f, c = 1) {
        switch (dof) {
            case OperationDOF.Y:
                return new Vector3(c, f, c);
            case OperationDOF.ZX:
                return new Vector3(f, c, f);
            case OperationDOF.Z:
                return new Vector3(c, c, f);
            case OperationDOF.XY:
                return new Vector3(f, f, c);
            case OperationDOF.X:
                return new Vector3(f, c, c);
            case OperationDOF.YZ:
                return new Vector3(c, f, f);
            case OperationDOF.XYZ:
                return new Vector3(f, f, f);
            case OperationDOF.NON:
            default:
                return new Vector3(c, c, c);
        }
    }
    _complement(dof) {
        switch (dof) {
            case OperationDOF.X:
                return OperationDOF.YZ;
            case OperationDOF.Y:
                return OperationDOF.ZX;
            case OperationDOF.Z:
                return OperationDOF.XY;
            case OperationDOF.XY:
                return OperationDOF.Z;
            case OperationDOF.YZ:
                return OperationDOF.X;
            case OperationDOF.ZX:
                return OperationDOF.Y;
            case OperationDOF.NON:
                return OperationDOF.XYZ;
            case OperationDOF.XYZ:
            default:
                return OperationDOF.NON;
        }
    }
    _extractComponents(v, dof) {
        switch (dof) {
            case OperationDOF.X:
                return [v.x];
            case OperationDOF.Y:
                return [v.y];
            case OperationDOF.Z:
                return [v.z];
            case OperationDOF.XY:
                return [v.x, v.y];
            case OperationDOF.YZ:
                return [v.y, v.z];
            case OperationDOF.ZX:
                return [v.z, v.x];
            case OperationDOF.NON:
                return [];
            case OperationDOF.XYZ:
            default:
                return [v.x, v.y, v.z];
        }
    }
    /**
     *
     */
    _followObject() {
        if (this._object != null) {
            // TODO : do i need this ??
            this._object.updateMatrix();
            this._object.updateMatrixWorld(true);
            const affine = new AffineTransform();
            affine.fromMatrix(this._object.matrixWorld);
            if (this._mode === GizmoMode.Global) {
                this._gizmo.quaternion.set(0, 0, 0, 1);
            }
            else if (this._mode === GizmoMode.View) {
                this._gizmo.quaternion.copy(this._camera.quaternion);
            }
            else {
                this._gizmo.quaternion.copy(affine.rotation);
            }
            this._gizmo.position.copy(affine.position);
        }
        else {
            // TODO : disable Gizmo
        }
        this._gizmo.updateMatrix();
        this._gizmo.updateMatrixWorld(true);
    }
    /**
     *
     */
    _pushUndo() {
        if (!this._object) {
            return;
        }
        console.trace();
        const affUndo = new AffineTransform();
        affUndo.copyFromObject3D(this._object);
        this._undoStack.push({ object: this._object, transform: affUndo });
    }
    /**
     *
     * @param object
     * @returns last selected object
     */
    _setObjectRaw(object) {
        const lastObj = this._object;
        this._gizmoState = (object == null) ? GizmoState.NeedsObject : this._gizmoState = GizmoState.Idle;
        this._object = object;
        this._followObject();
        return lastObj;
    }
    // ctx: GizmoKitContext
    _getPointer(dof, point, axis, rotation) {
        const pointer = new Pointer(this._camera);
        if (rotation) {
            pointer.setupPlaneMode(axis, point);
        }
        else {
            switch (dof) {
                case OperationDOF.X:
                case OperationDOF.Y:
                case OperationDOF.Z:
                    pointer.setupAxisMode(axis, point);
                    break;
                case OperationDOF.XY:
                case OperationDOF.YZ:
                case OperationDOF.ZX:
                    pointer.setupPlaneMode(axis, point);
                    break;
                default:
                    pointer.setupBasicMode(point);
            }
        }
        return pointer;
    }
}
//# sourceMappingURL=GizmoKit.js.map